import { Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { defaultStaticRanges, createStaticRanges } from "react-date-range";

import DownArrow from "../../../../styles/svg/date-range.svg";
import DateRange from "./DateRange";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { setDateRange } from "../../../../redux/SearchSlice";
import { useResponsive } from "../../../../hooks/useResponsive";
import { TooltipName } from "../../../../utils/Enums";
import CustomMenu from "../../../../components/Menu";
import "../../../myTask/myTask.scss";
import { CustomButton } from "../../../../components/Button/Button";

function SelectDatePicker() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const dispatch = useDispatch();
  const [oldDate] = useCustomSearchParams("date");
  const dateRange = useSelector((state) => state.search.dateRange);
  const open = Boolean(anchorEl);
  const date = JSON.parse(oldDate);
  const isMobile = useResponsive("down", "md");
  const [selectedDateLabel, setSelectedDateLabel] = useState("");

  useEffect(() => {
    if (date) {
      const obj = {
        startDate: new Date(date.startDate),
        endDate: new Date(date.endDate),
        key: "selection",
      };
      dispatch(setDateRange(obj));
    }

    if (!isMobile && dateRange) {
      return () => {
        dispatch(setDateRange(null));
      };
    }
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const heading = useMemo(() => {
    return dateRange
      ? `${moment(dateRange.startDate).format("MM/DD/YYYY")} - ${moment(
          dateRange.endDate
        ).format("MM/DD/YYYY")}`
      : "Select Date Range";
  }, [dateRange]);

  const handleSelectedLabel = useCallback((value) => {
    setSelectedDateLabel(value ?? "");
  }, []);

  const customStaticRanges = useMemo(
    () =>
      createStaticRanges([
        {
          label: "Last Week",
          range: () => {
            const startDate = moment()
              .subtract(1, "weeks")
              .startOf("week")
              .toDate(); // Start of last week
            const endDate = moment()
              .subtract(1, "weeks")
              .endOf("week")
              .toDate(); // End of last week

            return { startDate, endDate };
          },
        },
        {
          label: "Last 7 Days",
          range: () => ({
            startDate: moment().subtract(7, "days").toDate(),
            endDate: moment().toDate(),
          }),
        },
        {
          label: "Last 14 Days",
          range: () => ({
            startDate: moment().subtract(14, "days").toDate(),
            endDate: moment().toDate(),
          }),
        },
        {
          label: "Last 30 Days",
          range: () => ({
            startDate: moment().subtract(30, "days").toDate(),
            endDate: moment().toDate(),
          }),
        },
        {
          label: "Custom",
          range: () => ({
            startDate: new Date(new Date().setDate(new Date().getDate() - 3)),
            endDate: new Date(new Date().setDate(new Date().getDate() - 3)),
          }),
          hasCustomRendering: true,
        },
      ]),
    []
  );

  useEffect(() => {
    if (date) {
      const allRanges = [...defaultStaticRanges, ...customStaticRanges];
      const staticRange = allRanges.find(
        (range) =>
          moment(range.range().startDate).isSame(
            moment(date?.startDate),
            "day"
          ) &&
          moment(range.range().endDate).isSame(moment(date?.endDate), "day")
      );
      handleSelectedLabel(staticRange?.label);
    } else {
      handleSelectedLabel("");
    }
  }, [date, oldDate, customStaticRanges, handleSelectedLabel]);

  return (
    <>
      <Tooltip title={TooltipName.WORKLOG.FILTER_BY_DATE}>
        <CustomButton
          variant="outlined"
          color="primary"
          id="epics-board-dropdown"
          aria-haspopup="true"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          startIcon={<img src={DownArrow} />}
          className="white-button date-filter-button mobile-filter-date-button">
          <p className="header-action-button-text mr-1">
            {selectedDateLabel === "" || selectedDateLabel === undefined
              ? heading
              : selectedDateLabel}
          </p>
        </CustomButton>
      </Tooltip>
      <CustomMenu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        className="date-filter">
        <DateRange
          onClose={() => handleClose()}
          handleSelectedLabel={handleSelectedLabel}
          customStaticRanges={customStaticRanges}
        />
      </CustomMenu>
    </>
  );
}
export default SelectDatePicker;
